/**
 * La griglia dei widget. Fuori dalla modifica e' una griglia semplice; in
 * modifica i widget si trascinano prendendoli in qualunque punto (libreria
 * @dnd-kit, la stessa della bacheca dei lead e dell'editor email). Col dito
 * bisogna tenerli premuti un attimo: un tocco veloce fa scorrere la pagina.
 *
 * Come funziona il trascinamento con widget di taglie diverse:
 * - l'ordine cambia dal vivo mentre si trascina: la griglia vera si
 *   risistema e al posto del widget resta un segnaposto tratteggiato;
 * - i widget non vengono "stirati" per fare posto (nessuna trasformazione):
 *   con taglie diverse l'effetto sarebbe storto;
 * - si riordina solo se il puntatore si e' mosso di qualche pixel, non
 *   perche' la griglia gli si e' spostata sotto (niente sfarfallio);
 * - Esc annulla e rimette tutto com'era; da tastiera si usano le frecce.
 */

import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragMoveEvent,
  type DragOverEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, type SortingStrategy } from '@dnd-kit/sortable';
import { getEventCoordinates } from '@dnd-kit/utilities';
import type { AzioneLayout, LayoutDashboard, WidgetIstanza } from '../../lib/dashboard/layout';
import { puntatoreMosso, spostaSopra, type Punto } from '../../lib/dashboard/riordino';
import { CorniceWidget, type Trascinamento } from './CorniceWidget';
import { SegnapostoWidget } from './SegnapostoWidget';
import { CLASSI_GRIGLIA } from './taglie';
import { titoloWidget } from './titolo';
import type { ContestoWidget } from './widget/tipi';

interface Props {
  layout: LayoutDashboard;
  inModifica: boolean;
  dispatch: (azione: AzioneLayout) => void;
  contesto: ContestoWidget;
  onImpostazioni: (id: string) => void;
}

/** Nessuna trasformazione: la griglia si risistema da sola col nuovo ordine. */
const nessunaTrasformazione: SortingStrategy = () => null;

/** Col mouse o il dito conta cosa c'e' sotto il puntatore; da tastiera il piu' vicino. */
const rilevaBersaglio: CollisionDetection = (args) => (args.pointerCoordinates ? pointerWithin(args) : closestCenter(args));

/** In modifica il widget intero e' la maniglia, come le icone del telefono. */
function WidgetOrdinabile(props: Omit<Parameters<typeof CorniceWidget>[0], 'refNodo' | 'trascinamento' | 'segnaposto'>) {
  const { setNodeRef, attributes, listeners, isDragging } = useSortable({ id: props.widget.id });
  const trascinamento: Trascinamento = { attributi: attributes, ascoltatori: listeners };
  return <CorniceWidget {...props} refNodo={setNodeRef} trascinamento={trascinamento} segnaposto={isDragging} />;
}

function GrigliaOrdinabile({ layout, dispatch, contesto, onImpostazioni }: Omit<Props, 'inModifica'>) {
  const sensori = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = useMemo(() => layout.widget.map((w) => w.id), [layout.widget]);
  const perId = useMemo(() => new Map(layout.widget.map((w) => [w.id, w])), [layout.widget]);
  const [attivo, setAttivo] = useState<WidgetIstanza | null>(null);
  const ordineDiPartenza = useRef<string[] | null>(null);
  const partenza = useRef<Punto | null>(null);
  const puntatore = useRef<Punto | null>(null);
  const ultimoRiordino = useRef<Punto | null>(null);

  const titolo = (id: UniqueIdentifier) => {
    const w = perId.get(String(id));
    return w ? titoloWidget(w) : 'widget';
  };
  const annunci: Announcements = {
    onDragStart: ({ active }) => `Hai preso ${titolo(active.id)}.`,
    onDragOver: ({ active, over }) => (over ? `${titolo(active.id)} ora è al posto di ${titolo(over.id)}.` : `${titolo(active.id)} non è sopra un altro widget.`),
    onDragEnd: ({ active }) => `Hai lasciato ${titolo(active.id)}.`,
    onDragCancel: ({ active }) => `Spostamento annullato: ${titolo(active.id)} torna al suo posto.`,
  };

  const inizia = ({ active, activatorEvent }: DragStartEvent) => {
    setAttivo(perId.get(String(active.id)) ?? null);
    ordineDiPartenza.current = ids;
    const punto = getEventCoordinates(activatorEvent);
    partenza.current = punto;
    puntatore.current = punto;
    ultimoRiordino.current = punto;
  };
  const muovi = ({ delta }: DragMoveEvent) => {
    if (partenza.current) puntatore.current = { x: partenza.current.x + delta.x, y: partenza.current.y + delta.y };
  };
  const sopra = ({ active, over }: DragOverEvent) => {
    if (!over || over.id === active.id) return;
    if (!puntatoreMosso(ultimoRiordino.current, puntatore.current)) return;
    const ordine = spostaSopra(ids, String(active.id), String(over.id));
    if (ordine === ids) return;
    ultimoRiordino.current = puntatore.current;
    dispatch({ tipo: 'riordina', ordine });
  };
  const finisci = () => {
    setAttivo(null);
    ordineDiPartenza.current = null;
  };
  const annulla = () => {
    if (ordineDiPartenza.current) dispatch({ tipo: 'riordina', ordine: ordineDiPartenza.current });
    finisci();
  };

  return (
    <DndContext
      sensors={sensori}
      collisionDetection={rilevaBersaglio}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      autoScroll={{ threshold: { x: 0, y: 0.2 } }}
      accessibility={{
        announcements: annunci,
        screenReaderInstructions: {
          draggable: 'Per spostare un widget premi spazio o invio, muovilo con le frecce e premi di nuovo spazio o invio per lasciarlo. Esc annulla.',
        },
      }}
      onDragStart={inizia}
      onDragMove={muovi}
      onDragOver={sopra}
      onDragEnd={finisci}
      onDragCancel={annulla}
    >
      <SortableContext items={ids} strategy={nessunaTrasformazione}>
        <div className={CLASSI_GRIGLIA}>
          {layout.widget.map((w, i) => (
            <WidgetOrdinabile
              key={w.id}
              widget={w}
              contesto={contesto}
              inModifica
              dispatch={dispatch}
              onImpostazioni={onImpostazioni}
              indice={i}
              totale={layout.widget.length}
            />
          ))}
        </div>
      </SortableContext>
      {createPortal(
        <DragOverlay dropAnimation={null}>{attivo && <SegnapostoWidget widget={attivo} />}</DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

export function GrigliaWidget({ layout, inModifica, dispatch, contesto, onImpostazioni }: Props) {
  if (inModifica) {
    return <GrigliaOrdinabile layout={layout} dispatch={dispatch} contesto={contesto} onImpostazioni={onImpostazioni} />;
  }
  return (
    <div className={CLASSI_GRIGLIA}>
      {layout.widget.map((w, i) => (
        <CorniceWidget
          key={w.id}
          widget={w}
          contesto={contesto}
          inModifica={false}
          dispatch={dispatch}
          onImpostazioni={onImpostazioni}
          indice={i}
          totale={layout.widget.length}
        />
      ))}
    </div>
  );
}
